// 051 — datos discretos en FHIR (todo el formulario, no solo lo que ya tenía consumidor). Base común de los mapeos por sección.
//
// Modelo: cada sección del papel (antecedentes, gestación actual, controles prenatales, postparto, parto/aborto, morbilidad,
// intervenciones, near miss, recién nacido, egresos, anticoncepción) es un Observation "panel" con `hasMember` hacia sus
// Observation hijos. Los hijos agrupan las respuestas afines como `component` (una lista tri-estado de 12 ítems es UN Observation,
// no 12: así el Bundle de la transacción se mantiene chico). Lo que tiene consumidor propio va suelto:
//  - `Condition`  : morbilidad confirmada (con CIE-10 cuando el formulario trae el código), near miss, indicaciones de inducción/operatoria.
//  - `Procedure`  : intervenciones realizadas (episiotomía, cesárea/fórceps/vacuum, alumbramiento manual, laparotomía, histerectomía…).
//  - `Observation`: todo lo demás, con LOINC verificado en tx.fhir.org cuando existe (los códigos propios viven en `SISTEMA_051`).
//
// Reglas de guardado:
//  - Tri-estado: "Sí" -> valueBoolean true, "No" explícito -> valueBoolean false, sin responder -> no genera nada.
//  - "s/d" (sin dato) del papel -> `dataAbsentReason` = unknown (no es lo mismo que sin responder).
//  - Casillas de marcar / no marcar (un solo círculo): solo se guarda la marcada (true).
//  - Filas y grupos vacíos no generan recursos; los hijos de un padre en No / n/a / ninguna no se guardan (sin huérfanos).
//  - Un valor que no es número/fecha/hora no se pierde: pasa como `valueString` tal cual.
//  - Fecha efectiva: la del formulario cuando existe (AAAA-MM-DD válida: fecha de control, de nacimiento, de egreso…); si no, el
//    momento del guardado (`ahora`). Las horas sueltas del papel (sin zona horaria) van como component `valueTime`, sin inventar zona.
//  - Fuente de verdad de gestas/partos/abortos/cesáreas/FUM/FPP: la extensión `datos-gestacionales` del Patient (`../fhir051.ts`, con
//    consumidor propio); el Observation de antecedentes obstétricos repite esos valores con LOINC como parte de la historia del episodio.
//  - Gramos: "2.500" / "3,250" se leen como 2500 / 3250 g; un peso < 100 g en gramos se conserva como texto (dato dudoso, no una Quantity falsa).
//  - Los datos del recién nacido se registran en la HC de la madre (el RN no tiene Patient propio): llevan `category` =
//    `recien-nacido` (SISTEMA_051_SUJETO) para no confundirlos con datos maternos.
import type { CodeableConcept, Condition, Encounter, Observation, ObservationComponent, Patient, Procedure, Reference } from '@medplum/fhirtypes';
import type { SiNoValor } from '../camposSimplesDatos';
import type { Traducir } from '../tecnicaAnestesicaDatos';
import { idTemporal, type LoteTransaccional } from '../transaccionFhir';
import { leerTensionArterial, numeroDeTexto } from '../partograma/fhir';

export const SISTEMA_051 = 'http://logiacare.ec/fhir/perinatal-051';
export const SISTEMA_051_VALOR = 'http://logiacare.ec/fhir/perinatal-051-valor';
export const SISTEMA_051_MORBILIDAD = 'http://logiacare.ec/fhir/perinatal-051-morbilidad';
export const SISTEMA_051_PROCEDIMIENTO = 'http://logiacare.ec/fhir/perinatal-051-procedimiento';
export const SISTEMA_051_SUJETO = 'http://logiacare.ec/fhir/perinatal-051-sujeto';
const LOINC = 'http://loinc.org';
const UCUM = 'http://unitsofmeasure.org';
const SISTEMA_DATA_ABSENT_REASON = 'http://terminology.hl7.org/CodeSystem/data-absent-reason';

export interface Coding {
  readonly system: string;
  readonly code: string;
  readonly display?: string;
}

/** LOINC verificado (tx.fhir.org) o ya usado en el repo. */
export const loinc = (code: string, display: string): Coding => ({ system: LOINC, code, display });
/** Código propio de 051. */
export const local = (code: string, display: string): Coding => ({ system: SISTEMA_051, code, display });

export const LOINC_051 = {
  gestas: loinc('11996-6', '[#] Pregnancies'),
  partos: loinc('11977-6', '[#] Parity'),
  abortos: loinc('11612-9', '[#] Abortions'),
  fum: loinc('8665-2', 'Last menstrual period start date'),
  fpp: loinc('11778-8', 'Delivery date Estimated'),
  edadGestacional: loinc('11884-4', 'Gestational age Estimated'),
  peso: loinc('29463-7', 'Body weight'),
  talla: loinc('8302-2', 'Body height'),
  pesoAlNacer: loinc('8339-4', 'Birth weight Measured'),
  perimetroCefalico: loinc('9843-4', 'Head Occipital-frontal circumference'),
  apgar1: loinc('9272-6', '1 minute Apgar Score'),
  apgar5: loinc('9274-2', '5 minute Apgar Score'),
  apgar10: loinc('9271-8', '10 minute Apgar Score'),
  grupoAbo: loinc('883-9', 'ABO group [Type] in Blood'),
  rh: loinc('10331-7', 'Rh [Type] in Blood'),
  pulso: loinc('8867-4', 'Heart rate'),
  fr: loinc('9279-1', 'Respiratory rate'),
  temperatura: loinc('8310-5', 'Body temperature'),
  saturacion: loinc('59408-5', 'Oxygen saturation in Arterial blood by Pulse oximetry'),
  fcf: loinc('55283-6', 'Fetal Heart rate'),
  sistolica: loinc('8480-6', 'Systolic blood pressure'),
  diastolica: loinc('8462-4', 'Diastolic blood pressure'),
} as const;

export type Unidad = { readonly unit: string; readonly code: string };
export const U = {
  kg: { unit: 'kg', code: 'kg' },
  g: { unit: 'g', code: 'g' },
  cm: { unit: 'cm', code: 'cm' },
  semanas: { unit: 'sem', code: 'wk' },
  dias: { unit: 'd', code: 'd' },
  porMinuto: { unit: '/min', code: '/min' },
  celsius: { unit: '°C', code: 'Cel' },
  porcentaje: { unit: '%', code: '%' },
  mmHg: { unit: 'mm[Hg]', code: 'mm[Hg]' },
  gPorDl: { unit: 'g/dL', code: 'g/dL' },
  mgPorDl: { unit: 'mg/dL', code: 'mg/dL' },
} as const satisfies Record<string, Unidad>;

export function cuantidad(value: number, u: Unidad): { value: number; unit: string; system: string; code: string } {
  return { value, unit: u.unit, system: UCUM, code: u.code };
}

export interface Contexto051 {
  readonly lote: LoteTransaccional;
  readonly subject: Reference<Patient>;
  readonly encounter: Reference<Encounter> | undefined;
  /** Momento del guardado (ISO): fecha efectiva cuando el papel no trae una. */
  readonly efectivo: string;
  readonly t: Traducir;
}

export type Comp = ObservationComponent | undefined;

type CodigoObs = Coding | readonly Coding[];
const cc = (c: CodigoObs): CodeableConcept => ({ coding: (Array.isArray(c) ? (c as readonly Coding[]) : [c as Coding]).map((x) => ({ system: x.system, code: x.code, ...(x.display ? { display: x.display } : {}) })) });
const compDe = (code: Coding, resto: Partial<ObservationComponent>): ObservationComponent => ({ code: cc(code), ...resto });

/** AAAA-MM-DD que existe en el calendario. */
export function esFechaIso(texto: string): boolean {
  const s = texto.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(s);
}

/** "HH:MM" (o "H:MM") -> "HH:MM:00" (valueTime); `undefined` si no es una hora. */
export function horaValueTime(texto: string): string | undefined {
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(texto.trim());
  return m ? `${m[1]!.padStart(2, '0')}:${m[2]}:00` : undefined;
}

// ---------- Componentes ----------

/** Sí -> true, No -> false, sin responder -> nada. */
export function cSiNo(code: Coding, v: SiNoValor | string | undefined): Comp {
  return v === 'si' ? compDe(code, { valueBoolean: true }) : v === 'no' ? compDe(code, { valueBoolean: false }) : undefined;
}

/** Casilla de marcar / no marcar: solo la marcada se guarda. */
export function cMarca(code: Coding, marcada: boolean | undefined): Comp {
  return marcada ? compDe(code, { valueBoolean: true }) : undefined;
}

/** Sí / No / s/d: "s/d" (sin dato) se guarda como `dataAbsentReason` unknown. */
export function cSiNoSd(code: Coding, v: string | undefined): Comp {
  if (v === 'sd') return compDe(code, { dataAbsentReason: { coding: [{ system: SISTEMA_DATA_ABSENT_REASON, code: 'unknown', display: 'Unknown' }] } });
  return cSiNo(code, v);
}

export function valorOpcion(codigo: string, display: string): CodeableConcept {
  return { coding: [{ system: SISTEMA_051_VALOR, code: codigo, display }] };
}

/** Elección única (círculos): el código de la opción con su rótulo del formulario. */
export function cOpcion(code: Coding, opcion: string, display: string): Comp {
  return opcion ? compDe(code, { valueCodeableConcept: valorOpcion(opcion, display) }) : undefined;
}

export function cTexto(code: Coding, texto: string): Comp {
  const s = texto.trim();
  return s ? compDe(code, { valueString: s }) : undefined;
}

/**
 * Número del papel para una unidad. En gramos, "2.500" / "3,250" son separadores de miles (2500 g), no 2,5 g; y un peso < 100 g en
 * gramos es un dato dudoso (p. ej. kilos escritos en la casilla de gramos): se conserva como texto en vez de guardarlo como Quantity falsa.
 */
export function numeroConUnidad(texto: string, u: Unidad): number | undefined {
  let s = texto.trim();
  if (u.code === 'g' && /^\d{1,3}[.,]\d{3}$/.test(s)) s = s.replace(/[.,]/, '');
  const n = numeroDeTexto(s);
  if (n === undefined) return undefined;
  return u.code === 'g' && n < 100 ? undefined : n;
}

/** Número con unidad UCUM; si el texto no es un número, pasa como texto tal cual (no se pierde). */
export function cNumero(code: Coding, texto: string, u: Unidad): Comp {
  const s = texto.trim();
  if (!s) return undefined;
  const n = numeroConUnidad(s, u);
  return n === undefined ? compDe(code, { valueString: s }) : compDe(code, { valueQuantity: cuantidad(n, u) });
}

export function cEntero(code: Coding, texto: string): Comp {
  const s = texto.trim();
  if (!s) return undefined;
  return /^\d+$/.test(s) ? compDe(code, { valueInteger: Number(s) }) : compDe(code, { valueString: s });
}

export function cFecha(code: Coding, texto: string): Comp {
  const s = texto.trim();
  if (!s) return undefined;
  return esFechaIso(s) ? compDe(code, { valueDateTime: s }) : compDe(code, { valueString: s });
}

export function cHora(code: Coding, texto: string): Comp {
  const s = texto.trim();
  if (!s) return undefined;
  const h = horaValueTime(s);
  return h ? compDe(code, { valueTime: h }) : compDe(code, { valueString: s });
}

/** "120/80" -> sistólica y diastólica (LOINC 8480-6 / 8462-4); otro formato -> texto bajo el código del panel de TA (85354-9). */
export function cTension(texto: string): Comp[] {
  const s = texto.trim();
  if (!s) return [];
  const par = leerTensionArterial(s);
  if (!par) return [compDe(loinc('85354-9', 'Blood pressure panel with all children optional'), { valueString: s })];
  return [compDe(LOINC_051.sistolica, { valueQuantity: cuantidad(par.sistolica, U.mmHg) }), compDe(LOINC_051.diastolica, { valueQuantity: cuantidad(par.diastolica, U.mmHg) })];
}

/** Valor numérico simple para Observation completo (peso, talla…): `valueQuantity` o, si no es número, `valueString`. */
export function valorNumerico(texto: string, u: Unidad): Pick<Observation, 'valueQuantity' | 'valueString'> | undefined {
  const s = texto.trim();
  if (!s) return undefined;
  const n = numeroConUnidad(s, u);
  return n === undefined ? { valueString: s } : { valueQuantity: cuantidad(n, u) };
}

const definido = (c: Comp): c is ObservationComponent => c !== undefined;
export const sinVacios = (comps: readonly (Comp | readonly Comp[])[]): ObservationComponent[] => comps.flat().filter(definido);

// ---------- Sección (panel + miembros) ----------

const categoria = (codigo: string, display: string): CodeableConcept => ({ coding: [{ system: SISTEMA_051_SUJETO, code: codigo, display }] });
export const CATEGORIA_RECIEN_NACIDO: CodeableConcept = categoria('recien-nacido', 'Recién nacido (dato del hijo, registrado en la historia de la madre)');

/** Agrupa los Observation de una sección bajo un panel con `hasMember`. Sin ningún miembro no hay panel. */
export class Seccion051 {
  private readonly miembros: Reference<Observation>[] = [];

  constructor(
    readonly ctx: Contexto051,
    private readonly codigoPanel: Coding,
    private readonly categoriaSeccion?: CodeableConcept
  ) {}

  /** Observation suelto (con valor propio) como miembro de la sección. */
  observacion(o: Pick<Observation, 'code'> & Partial<Observation>): Observation {
    const fullUrl = idTemporal();
    const obs = this.ctx.lote.crear<Observation>(
      {
        resourceType: 'Observation',
        status: 'final',
        subject: this.ctx.subject,
        encounter: this.ctx.encounter,
        effectiveDateTime: this.ctx.efectivo,
        ...(this.categoriaSeccion ? { category: [this.categoriaSeccion] } : {}),
        ...o,
      },
      fullUrl
    );
    this.miembros.push({ reference: fullUrl });
    return obs;
  }

  /** Observation con `component`s; no se crea si no queda ningún componente (ni valor en `extra`). */
  grupo(codigo: CodigoObs, comps: readonly (Comp | readonly Comp[])[], extra: Partial<Observation> = {}): Observation | undefined {
    const component = sinVacios(comps);
    const tieneValor = Object.keys(extra).some((k) => k.startsWith('value'));
    if (component.length === 0 && !tieneValor) return undefined;
    return this.observacion({ code: cc(codigo), ...extra, ...(component.length > 0 ? { component } : {}) });
  }

  /** Registra como miembro un Observation ya creado en el lote con este `fullUrl`. */
  adoptar(fullUrl: string): void {
    this.miembros.push({ reference: fullUrl });
  }

  get cantidad(): number {
    return this.miembros.length;
  }

  cerrar(): Observation | undefined {
    if (this.miembros.length === 0) return undefined;
    return this.ctx.lote.crear<Observation>({
      resourceType: 'Observation',
      status: 'final',
      code: cc(this.codigoPanel),
      subject: this.ctx.subject,
      encounter: this.ctx.encounter,
      effectiveDateTime: this.ctx.efectivo,
      ...(this.categoriaSeccion ? { category: [this.categoriaSeccion] } : {}),
      hasMember: this.miembros,
    });
  }
}

// ---------- Condition y Procedure ----------

const CATEGORIA_CONDITION_DX: CodeableConcept = { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-category', code: 'encounter-diagnosis', display: 'Encounter Diagnosis' }] };

/** Condition confirmada del episodio (con clinicalStatus porque lleva category encounter-diagnosis: invariante con-3). */
export function crearCondicion(ctx: Contexto051, code: CodeableConcept, fecha?: string): Condition {
  return ctx.lote.crear<Condition>({
    resourceType: 'Condition',
    clinicalStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'active', display: 'Active' }] },
    verificationStatus: { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status', code: 'confirmed', display: 'Confirmed' }] },
    category: [CATEGORIA_CONDITION_DX],
    code,
    subject: ctx.subject,
    encounter: ctx.encounter,
    recordedDate: fecha && esFechaIso(fecha) ? fecha : ctx.efectivo,
  });
}

/** Procedure realizada; `performedDateTime` = la fecha del papel si existe, si no el momento del guardado. */
export function crearProcedimiento(ctx: Contexto051, codigo: string, display: string, fecha?: string, nota?: string): Procedure {
  return ctx.lote.crear<Procedure>({
    resourceType: 'Procedure',
    status: 'completed',
    code: { coding: [{ system: SISTEMA_051_PROCEDIMIENTO, code: codigo, display }], text: display },
    subject: ctx.subject,
    encounter: ctx.encounter,
    performedDateTime: fecha && esFechaIso(fecha) ? fecha : ctx.efectivo,
    ...(nota?.trim() ? { note: [{ text: nota.trim() }] } : {}),
  });
}

/** Evita crear dos veces la misma Procedure cuando la repiten dos secciones del papel (p. ej. hemoderivados en intervenciones y near miss). */
export class ProcedimientosCreados {
  private readonly vistos = new Set<string>();
  /** `fechaPorDefecto`: fecha del nacimiento del papel (AAAA-MM-DD) para las Procedure sin fecha propia; si no es válida, el momento del guardado. */
  constructor(private readonly fechaPorDefecto?: string) {}
  crear(ctx: Contexto051, codigo: string, display: string, fecha?: string, nota?: string): void {
    if (this.vistos.has(codigo)) return;
    this.vistos.add(codigo);
    crearProcedimiento(ctx, codigo, display, fecha && esFechaIso(fecha) ? fecha : this.fechaPorDefecto, nota);
  }
}

export { cc as conceptoDe };
